import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, Route, Routes, useBlocker, useNavigate, useParams } from 'react-router';
import {
  KIOSK_TEXTS,
  VISITOR_LANGUAGES,
  VISITOR_TEXTS,
  eventName,
  type VisitorLanguage,
} from '@wr/shared';
import { defaults, kioskPin, privacy } from '../../../../../config/portal.json';
import { errorMessage, get, put } from '../../api.js';
import { useCurrentEvent } from '../../components/CurrentEvent.js';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { Checkbox, Dialog, EmptyState, Field, Notice, PageHead } from '../../components/ui.js';
import { validateContact } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import { useSignedIn } from '../../session.js';
import { PinPad } from '../capture/PinPad.js';
import { Thumb, type Asset } from '../content/ContentScreen.js';
import { PdfOverlay, VideoOverlay, Viewer } from '../content/Viewers.js';
import {
  keptBytes,
  keptImage,
  keptItems,
  playableUrl,
  preparation,
  reconcile,
  type KeptItem,
} from '../../offline/media.js';
import { useOnline } from '../../offline/network.js';
import { fileBytes, fileUrl, imageUrl, loadKiosk, thumbnailUrl, type KioskItem } from './data.js';
import { KioskPrepare } from './KioskPrepare.js';

/** Back home after this long without a touch (brief section 7.5). */
const IDLE_MS = defaults.kioskIdleSeconds * 1000;
const THANKS_MS = 7_000;

/**
 * Booth kiosk (brief section 7.5, docs/prototype.html), mounted at
 * /kiosk/*: the current event's, or an event's from Events (without
 * changing the current event). Only the event's selected content: Watch,
 * Read, Receive info. It works offline; it leaves only with the kiosk PIN.
 */
export function KioskScreen() {
  return (
    <Routes>
      <Route index element={<KioskCurrent />} />
      <Route path=":eventId" element={<KioskMode />} />
    </Routes>
  );
}

function KioskCurrent() {
  const { eventId } = useCurrentEvent();
  if (eventId === undefined) return <div className="spinner" aria-label="Loading" />;
  if (eventId) return <Navigate to={eventId} replace />;
  return (
    <>
      <PageHead title="Booth kiosk" />
      <EmptyState title="No current event.">
        The kiosk shows the current event's content. Pick the current event first, or start the
        kiosk from its card in Events.
      </EmptyState>
    </>
  );
}

type Screen = 'home' | 'videos' | 'docs' | 'email' | 'thanks' | 'pin';

/** The event's kiosk content, then the kiosk; without content, what to do. */
function KioskMode() {
  const { eventId = '' } = useParams();
  const { store } = useOffline();
  const { events } = useCurrentEvent();
  const { can, user } = useSignedIn();
  const online = useOnline();
  const [items, setItems] = useState<KioskItem[] | null>(null);
  const [kept, setKept] = useState<Record<string, KeptItem> | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // Round 2: once the content is downloaded (or the person starts without it), the kiosk runs.
  const [preparing, setPreparing] = useState<boolean | null>(null);
  const event = events.find((e) => e.id === eventId);

  useEffect(() => {
    let current = true;
    loadKiosk(store, eventId).then(
      async (list) => {
        // What this device keeps of it; a new version is only noted (never downloaded by itself).
        // The device's store unreadable (closed meanwhile): nothing kept, the kiosk still runs.
        const found = !store
          ? {}
          : await (navigator.onLine ? reconcile(store, user.id, list, true) : keptItems(store))
              .catch(() => keptItems(store))
              .catch(() => ({}));
        if (!current) return;
        setItems(list);
        setKept(found);
      },
      (err: unknown) => current && setFailed(errorMessage(err)),
    );
    return () => {
      current = false;
    };
  }, [store, eventId, user.id]);

  const name = event ? eventName(event) : 'this event';
  if (items && items.length > 0 && kept) {
    // Not ready offline, with network: the download is offered first, with its size.
    const prepare = preparing ?? (online && !preparation(items, kept).ready);
    if (prepare) {
      return (
        <KioskPrepare
          eventId={eventId}
          name={name}
          items={items}
          kept={kept}
          onKept={(k) => {
            setKept(k);
            setPreparing(true);
          }}
          onStart={() => setPreparing(false)}
        />
      );
    }
    return <KioskRun eventId={eventId} items={items} kept={kept} />;
  }
  return (
    <>
      <PageHead title="Booth kiosk" />
      {failed && <Notice kind="error">{failed}</Notice>}
      {!items && !failed && <div className="spinner" aria-label="Loading" />}
      {items && (
        <EmptyState title={`No booth kiosk content for ${name}.`}>
          {can('kiosk.content.manage')
            ? 'Choose it first, in Events: Booth kiosk content.'
            : 'An administrator chooses it first, in Events.'}{' '}
          <Link to="/events">Open Events</Link>
        </EmptyState>
      )}
    </>
  );
}

/** The kiosk itself, for the next visitor. */
function KioskRun({
  eventId,
  items,
  kept,
}: {
  eventId: string;
  items: KioskItem[];
  kept: Record<string, KeptItem>;
}) {
  const { save } = useOffline();
  const { user } = useSignedIn();
  const online = useOnline();
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>('home');
  const [from, setFrom] = useState<Screen>('home');
  const [lang, setLang] = useState<VisitorLanguage>('en');
  const [picked, setPicked] = useState<string[]>([]);
  const [seen, setSeen] = useState<string[]>([]);
  const [open, setOpen] = useState<KioskItem | null>(null);
  const leaving = useRef(false);
  const T = KIOSK_TEXTS[lang];
  // The open PDF's bytes: the device's copy first (a show without network).
  const openId = open?.id;
  const loadOpen = useCallback(
    async () =>
      (openId ? await keptBytes(user.id, openId) : null) ??
      fileBytes(fileUrl(eventId, openId ?? '')),
    [eventId, openId, user.id],
  );

  // The visitor never leaves by Back or a link: the PIN first.
  const blocker = useBlocker(() => !leaving.current);
  const blocked = blocker.state === 'blocked';
  useEffect(() => {
    const onLeave = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onLeave);
    document.body.classList.add('kiosk-mode');
    return () => {
      window.removeEventListener('beforeunload', onLeave);
      document.body.classList.remove('kiosk-mode');
    };
  }, []);

  const reset = useCallback(() => {
    setOpen(null);
    setPicked([]);
    setSeen([]);
    setLang('en');
    setScreen('home');
  }, []);

  // 90 seconds without a touch: back home, for the next visitor.
  useEffect(() => {
    if (screen === 'home' || screen === 'pin') return;
    let timer = setTimeout(reset, IDLE_MS);
    const touch = () => {
      clearTimeout(timer);
      timer = setTimeout(reset, IDLE_MS);
    };
    const kinds = ['pointerdown', 'keydown', 'scroll'] as const;
    kinds.forEach((k) => window.addEventListener(k, touch, true));
    return () => {
      clearTimeout(timer);
      kinds.forEach((k) => window.removeEventListener(k, touch, true));
    };
  }, [screen, reset]);

  useEffect(() => {
    if (screen !== 'thanks') return;
    const timer = setTimeout(reset, THANKS_MS);
    return () => clearTimeout(timer);
  }, [screen, reset]);

  const unlock = useCallback(() => {
    if (blocker.state === 'blocked') blocker.reset();
    leaving.current = true;
    navigate('/events', { replace: true });
  }, [navigate, blocker]);

  const lock = (
    <button
      type="button"
      className="k-lock"
      aria-label="Exit kiosk mode"
      onClick={() => {
        setFrom(screen);
        setScreen('pin');
      }}
    >
      🔒
    </button>
  );

  if (screen === 'pin' || blocked) {
    return (
      <div className="kiosk">
        <div className="k-top">
          <img src="/logo.png" alt="Waste Robotics" />
          <button type="button" className="k-lock" aria-label="Exit kiosk mode" disabled>
            🔒
          </button>
        </div>
        <PinPad
          digits={kioskPin.digits}
          mode="kiosk"
          onUnlock={unlock}
          onBack={() => (blocked ? blocker.reset() : setScreen(from === 'pin' ? 'home' : from))}
        />
      </div>
    );
  }

  const top = (
    <div className="k-top">
      <img src="/logo.png" alt="Waste Robotics" />
      {lock}
    </div>
  );

  // Without network, a video not kept on this device is hidden (round 2).
  const videos = items.filter((i) => i.format === 'video' && (online || kept[i.id]));
  const docs = items.filter((i) => i.format === 'pdf');
  const toggle = (id: string) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const show = (item: KioskItem) => {
    setOpen(item);
    setSeen((s) => (s.includes(item.id) ? s : [...s, item.id]));
  };

  const overlay =
    open &&
    (open.format === 'video' && !online ? (
      <KeptVideo
        userId={user.id}
        id={open.id}
        title={open.title}
        closeLabel={T.close}
        onClose={() => setOpen(null)}
      />
    ) : open.format === 'video' && open.provider && open.video_id ? (
      <VideoOverlay
        title={open.title}
        closeLabel={T.close}
        video={{ provider: open.provider, id: open.video_id, hash: open.video_hash }}
        onClose={() => setOpen(null)}
      />
    ) : (
      <PdfOverlay
        title={open.title}
        url={fileUrl(eventId, open.id)}
        closeLabel={T.close}
        load={loadOpen}
        texts={{ opening: T.opening, failed: T.cannotShow, download: T.close }}
        onClose={() => setOpen(null)}
        onDownload={() => setOpen(null)}
      />
    ));

  if (screen === 'thanks') {
    return (
      <div className="kiosk k-attract">
        {top}
        <div className="k-home">
          <span className="okdot" style={{ width: 64, height: 64 }} />
          <h1>{T.thanks}</h1>
          <p>{T.thanksSub}</p>
        </div>
      </div>
    );
  }

  if (screen === 'videos' || screen === 'docs') {
    const list = screen === 'videos' ? videos : docs;
    return (
      <div className="kiosk" lang={lang}>
        {top}
        <div className="k-body">
          <button type="button" className="linkbtn" onClick={() => setScreen('home')}>
            ← {T.back}
          </button>
          <h2>{screen === 'videos' ? T.videosTitle : T.documentsTitle}</h2>
          <div className="media-grid">
            {list.map((item) => (
              <article key={item.id} className="tile">
                <button
                  type="button"
                  className="tile-media"
                  aria-label={item.title}
                  onClick={() => show(item)}
                >
                  <KioskThumb eventId={eventId} item={item} />
                </button>
                <h3>{item.title}</h3>
                <button
                  type="button"
                  className={`btn block${picked.includes(item.id) ? ' dark' : ''}`}
                  aria-pressed={picked.includes(item.id)}
                  onClick={() => toggle(item.id)}
                >
                  {picked.includes(item.id) ? T.added : T.add}
                </button>
              </article>
            ))}
          </div>
          <button type="button" className="btn primary huge" onClick={() => setScreen('email')}>
            {T.receive}
            {picked.length ? ` (${picked.length})` : ''}
          </button>
        </div>
        {overlay}
      </div>
    );
  }

  if (screen === 'email') {
    return (
      <div className="kiosk" lang={lang}>
        {top}
        <ReceiveInfo
          eventId={eventId}
          items={items}
          preset={[...new Set([...picked, ...seen])]}
          lang={lang}
          onLang={setLang}
          onBack={() => setScreen('home')}
          onSend={async (contact) => {
            await save({
              opId: newId(),
              entity: 'contact',
              id: newId(),
              action: 'upsert',
              changedAt: new Date().toISOString(),
              fields: {
                ...contact,
                source: 'kiosk_visitor',
                event_id: eventId,
                consent_at: new Date().toISOString(),
                consent_text_version: privacy.consentVersion,
              },
            });
            setScreen('thanks');
          }}
        />
      </div>
    );
  }

  return (
    <div className="kiosk k-attract" lang={lang}>
      {top}
      <div className="k-home">
        <h1>{T.headline}</h1>
        <p>{T.homeSub}</p>
        <div className="k-langs" role="group" aria-label="Language">
          {VISITOR_LANGUAGES.map((l) => (
            <button
              key={l}
              type="button"
              lang={l}
              aria-pressed={l === lang}
              onClick={() => setLang(l)}
            >
              {VISITOR_TEXTS[l].language}
            </button>
          ))}
        </div>
        <div className="k-tiles">
          {videos.length > 0 && (
            <button type="button" onClick={() => setScreen('videos')}>
              <Icon name="play" />
              <b>{T.watch}</b>
              <span>{T.videos(videos.length)}</span>
            </button>
          )}
          {docs.length > 0 && (
            <button type="button" onClick={() => setScreen('docs')}>
              <Icon name="pdf" />
              <b>{T.read}</b>
              <span>{T.documents(docs.length)}</span>
            </button>
          )}
          <button type="button" className="red" onClick={() => setScreen('email')}>
            <Icon name="mail" />
            <b>{T.receive}</b>
            <span>{picked.length ? T.selected(picked.length) : T.chooseWhat}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/** A video kept on this device, played without network (round 2). */
function KeptVideo({
  userId,
  id,
  title,
  closeLabel,
  onClose,
}: {
  userId: string;
  id: string;
  title: string;
  closeLabel: string;
  onClose(): void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    void playableUrl(userId, id).then((u) => current && setSrc(u));
    return () => {
      current = false;
    };
  }, [userId, id]);
  return (
    <Viewer title={title} className="player" closeLabel={closeLabel} onClose={onClose}>
      <div className="pstage">
        {src && <video className="pframe" src={src} controls autoPlay playsInline />}
      </div>
    </Viewer>
  );
}

/** A kiosk item's thumbnail, from the device's copy when it keeps one. */
function KioskThumb({ eventId, item }: { eventId: string; item: KioskItem }) {
  const { user } = useSignedIn();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!item.has_thumbnail) return;
    let current = true;
    void (async () => {
      const url =
        (await keptImage(user.id, item.id)) || (await imageUrl(thumbnailUrl(eventId, item.id)));
      if (current) setSrc(url);
    })();
    return () => {
      current = false;
    };
  }, [eventId, item, user.id]);
  const asset = {
    ...item,
    has_thumbnail: false,
    tags: [],
    products: [],
  } as unknown as Asset;
  if (!src) return <Thumb asset={asset} />;
  return item.format === 'video' ? (
    <span className="thumb-wrap">
      <img className="thumb-img" src={src} alt="" />
      <span className="playdot" />
    </span>
  ) : (
    <img className="thumb-img top" src={src} alt="" />
  );
}

interface Visitor {
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  note: string;
  kiosk_asset_ids: string[];
  kiosk_lang: VisitorLanguage;
}

/** Receive info: what to receive (added or viewed ones ticked), the contact and the consent. */
function ReceiveInfo({
  eventId,
  items,
  preset,
  lang,
  onLang,
  onBack,
  onSend,
}: {
  eventId: string;
  items: KioskItem[];
  preset: string[];
  lang: VisitorLanguage;
  onLang(l: VisitorLanguage): void;
  onBack(): void;
  onSend(visitor: Visitor): Promise<void>;
}) {
  const T = KIOSK_TEXTS[lang];
  const V = VISITOR_TEXTS[lang];
  const [chosen, setChosen] = useState<string[]>(preset);
  const [draft, setDraft] = useState({ first_name: '', last_name: '', company: '', email: '' });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (key: keyof typeof draft, v: string) => {
    setDraft((d) => ({ ...d, [key]: v }));
    setError('');
  };

  async function send(e: FormEvent) {
    e.preventDefault();
    if (chosen.length === 0) return setError(T.pickOne);
    if (!validateContact(draft).ok) return setError(T.fillIn);
    if (!consent) return setError(V.consentNeeded);
    setBusy(true);
    try {
      const titles = items.filter((i) => chosen.includes(i.id)).map((i) => i.title);
      await onSend({
        ...draft,
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
        company: draft.company.trim(),
        email: draft.email.trim(),
        note: `Booth kiosk. Asked to receive: ${titles.join(', ')}.`,
        kiosk_asset_ids: items.filter((i) => chosen.includes(i.id)).map((i) => i.id),
        kiosk_lang: lang,
      });
    } catch {
      setError('This device could not save. Please call the Waste Robotics rep.');
      setBusy(false);
    }
  }

  return (
    <form className="k-body k-form" noValidate onSubmit={(e) => void send(e)}>
      <button type="button" className="linkbtn" onClick={onBack}>
        ← {T.back}
      </button>
      <div className="k-langs" role="group" aria-label="Language">
        {VISITOR_LANGUAGES.map((l) => (
          <button
            key={l}
            type="button"
            lang={l}
            aria-pressed={l === lang}
            onClick={() => onLang(l)}
          >
            {VISITOR_TEXTS[l].language}
          </button>
        ))}
      </div>
      <h2>{T.receive}</h2>
      <p className="note">{T.receiveSub}</p>
      <div className="k-pickall">
        <b>{T.whatToReceive}</b>
        <button type="button" className="linkbtn" onClick={() => setChosen(items.map((i) => i.id))}>
          {T.selectAll}
        </button>
      </div>
      <div className="kc-list">
        {items.map((item) => (
          <label key={item.id} className="kc-it">
            <input
              type="checkbox"
              checked={chosen.includes(item.id)}
              onChange={(e) =>
                setChosen((c) =>
                  e.target.checked ? [...c, item.id] : c.filter((x) => x !== item.id),
                )
              }
            />
            <span className="kc-th">
              <KioskThumb eventId={eventId} item={item} />
            </span>
            <span className="kc-tx">
              <span className="tt">{item.title}</span>
              <small className="note">{item.format === 'video' ? T.video : T.document}</small>
            </span>
          </label>
        ))}
      </div>
      <div className="g2">
        <Field
          label={V.firstName}
          mark
          autoComplete="off"
          value={draft.first_name}
          onChange={(e) => set('first_name', e.target.value)}
        />
        <Field
          label={V.lastName}
          mark
          autoComplete="off"
          value={draft.last_name}
          onChange={(e) => set('last_name', e.target.value)}
        />
      </div>
      <div className="g2">
        <Field
          label={V.company}
          mark
          autoComplete="off"
          value={draft.company}
          onChange={(e) => set('company', e.target.value)}
        />
        <Field
          label={T.workEmail}
          mark
          type="email"
          inputMode="email"
          autoComplete="off"
          value={draft.email}
          onChange={(e) => set('email', e.target.value)}
        />
      </div>
      <Checkbox
        checked={consent}
        onChange={(e) => {
          setConsent(e.target.checked);
          setError('');
        }}
      >
        {V.consent}{' '}
        <span className="req" aria-hidden="true">
          *
        </span>
        {privacy.policyUrl && (
          <>
            {' '}
            <a href={privacy.policyUrl} target="_blank" rel="noreferrer">
              {V.privacy}
            </a>
          </>
        )}
      </Checkbox>
      <p className="k-err" role="alert">
        {error}
      </p>
      <button type="submit" className="btn primary huge" disabled={busy}>
        {T.receive}
      </button>
    </form>
  );
}

/**
 * Booth kiosk content (admins, brief section 7.1): the event's items for
 * the booth tablet, in the order they are ticked (prototype: openKioskContent).
 */
export function KioskContentWindow({
  event,
  onClose,
}: {
  event: { id: string; series?: unknown; year?: unknown; kiosk_asset_ids?: unknown };
  onClose(saved: boolean): void;
}) {
  const toast = useToast();
  const [library, setLibrary] = useState<Asset[] | null>(null);
  const [selected, setSelected] = useState<string[]>(
    Array.isArray(event.kiosk_asset_ids) ? (event.kiosk_asset_ids as string[]) : [],
  );
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const name = eventName(event);

  useEffect(() => {
    let current = true;
    get<{ items: Asset[] }>('/content')
      .then(({ items }) => current && setLibrary(items))
      .catch((err: unknown) => current && setError(errorMessage(err)));
    return () => {
      current = false;
    };
  }, []);

  const shown = useMemo(
    () =>
      (library ?? []).filter((a) =>
        `${a.title} ${a.lang} ${a.tags.join(' ')} ${a.products.join(' ')}`
          .toLowerCase()
          .includes(q.trim().toLowerCase()),
      ),
    [library, q],
  );

  const noCopy = (library ?? []).filter(
    (a) => selected.includes(a.id) && a.format === 'video' && !a.has_offline,
  ).length;

  async function saveContent() {
    try {
      await put(`/kiosk/events/${event.id}/content`, { asset_ids: selected });
      toast(`Booth kiosk content saved for ${name}`);
      onClose(true);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <Dialog
      title={`Booth kiosk content: ${name}`}
      className="wide"
      onClose={() => onClose(false)}
      footer={
        <>
          <button type="button" className="btn" onClick={() => onClose(false)}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={() => void saveContent()}>
            Save
          </button>
        </>
      }
    >
      <p className="wrap-intro">
        Only these items appear on the booth tablet during {name}, in the order you tick them.
      </p>
      <input
        type="search"
        placeholder="Search title, language, product"
        aria-label="Search content"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <p className="note wrap-intro">{selected.length} selected</p>
      {noCopy > 0 && (
        <Notice kind="warn">
          {noCopy === 1 ? '1 video chosen has' : `${noCopy} videos chosen have`} no offline copy:
          without network at the show, the kiosk hides {noCopy === 1 ? 'it' : 'them'}. Add an
          offline copy in Content.
        </Notice>
      )}
      {!library && !error && <div className="spinner" aria-label="Loading" />}
      <div className="kc-list">
        {shown.map((a) => {
          const n = selected.indexOf(a.id);
          return (
            <label key={a.id} className="kc-it">
              <input
                type="checkbox"
                checked={n >= 0}
                onChange={(e) =>
                  setSelected((s) =>
                    e.target.checked ? [...s, a.id] : s.filter((x) => x !== a.id),
                  )
                }
              />
              <span className="kc-n">{n >= 0 ? n + 1 : ''}</span>
              <span className="kc-th">
                <Thumb asset={a} />
              </span>
              <span className="kc-tx">
                <span className="tt">{a.title}</span>
                <small className="note">
                  {[
                    a.format === 'video' ? 'Video' : 'PDF',
                    a.lang.toUpperCase(),
                    ...a.products,
                  ].join(', ')}
                </small>
                {/* Round 2: hidden on the booth tablet without network. */}
                {a.format === 'video' && !a.has_offline && (
                  <small className="note offline-none">
                    No offline copy: hidden in the kiosk without network
                  </small>
                )}
              </span>
            </label>
          );
        })}
      </div>
      {error && <Notice kind="error">{error}</Notice>}
    </Dialog>
  );
}
